import { Network } from '@/Overtime/enums/network'
import type { ContractData } from '@/Overtime/types/viem'

const sgpTradingProcessorContract: ContractData = {
  addresses: {
    [Network.OptimismMainnet]: '0x27A4403ba34d5FB8dA30bCBA455837FbF9aa1666',
    [Network.Arbitrum]: '0xcC2e7b42cB21386FD71dECB22ebd429754B505F2',
    [Network.Base]: '0xB908ED8efA7E37304602f2782674eE0B6CD19D47',
    [Network.OptimismSepolia]: '0x1B3b991Da49DBCAbf5b5DA401E8a13946bBedEBD',
  },
  abi: [
    {
      inputs: [
        { internalType: 'address', name: '_link', type: 'address' },
        { internalType: 'address', name: '_oracle', type: 'address' },
        { internalType: 'address', name: '_sportsAMM', type: 'address' },
        { internalType: 'bytes32', name: '_jobSpecId', type: 'bytes32' },
        { internalType: 'uint256', name: '_paymentAmount', type: 'uint256' },
      ],
      stateMutability: 'nonpayable',
      type: 'constructor',
    },
    {
      inputs: [{ internalType: 'address', name: 'target', type: 'address' }],
      name: 'AddressEmptyCode',
      type: 'error',
    },
    {
      inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
      name: 'AddressInsufficientBalance',
      type: 'error',
    },
    { inputs: [], name: 'EnforcedPause', type: 'error' },
    { inputs: [], name: 'ExpectedPause', type: 'error' },
    { inputs: [], name: 'FailedInnerCall', type: 'error' },
    {
      inputs: [{ internalType: 'address', name: 'owner', type: 'address' }],
      name: 'OwnableInvalidOwner',
      type: 'error',
    },
    {
      inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
      name: 'OwnableUnauthorizedAccount',
      type: 'error',
    },
    {
      inputs: [{ internalType: 'address', name: 'token', type: 'address' }],
      name: 'SafeERC20FailedOperation',
      type: 'error',
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'bytes32', name: 'id', type: 'bytes32' },
      ],
      name: 'ChainlinkCancelled',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'bytes32', name: 'id', type: 'bytes32' },
      ],
      name: 'ChainlinkFulfilled',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: 'bytes32', name: 'id', type: 'bytes32' },
      ],
      name: 'ChainlinkRequested',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'address',
          name: '_link',
          type: 'address',
        },
        {
          indexed: false,
          internalType: 'address',
          name: '_oracle',
          type: 'address',
        },
        {
          indexed: false,
          internalType: 'address',
          name: '_sportsAMM',
          type: 'address',
        },
        {
          indexed: false,
          internalType: 'bytes32',
          name: '_jobSpecId',
          type: 'bytes32',
        },
        {
          indexed: false,
          internalType: 'uint256',
          name: '_paymentAmount',
          type: 'uint256',
        },
      ],
      name: 'ContextReset',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: true,
          internalType: 'address',
          name: 'previousOwner',
          type: 'address',
        },
        {
          indexed: true,
          internalType: 'address',
          name: 'newOwner',
          type: 'address',
        },
      ],
      name: 'OwnershipTransferred',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'address',
          name: 'account',
          type: 'address',
        },
      ],
      name: 'Paused',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'address',
          name: 'requester',
          type: 'address',
        },
        {
          indexed: false,
          internalType: 'bytes32',
          name: 'requestId',
          type: 'bytes32',
        },
        { indexed: false, internalType: 'bool', name: '_allow', type: 'bool' },
        {
          components: [
            {
              components: [
                { internalType: 'bytes32', name: 'gameId', type: 'bytes32' },
                { internalType: 'uint16', name: 'sportId', type: 'uint16' },
                { internalType: 'uint16', name: 'typeId', type: 'uint16' },
                { internalType: 'uint256', name: 'maturity', type: 'uint256' },
                { internalType: 'uint8', name: 'status', type: 'uint8' },
                { internalType: 'int24', name: 'line', type: 'int24' },
                { internalType: 'uint24', name: 'playerId', type: 'uint24' },
                { internalType: 'uint256[]', name: 'odds', type: 'uint256[]' },
                {
                  internalType: 'bytes32[]',
                  name: 'merkleProof',
                  type: 'bytes32[]',
                },
                { internalType: 'uint8', name: 'position', type: 'uint8' },
                {
                  components: [
                    { internalType: 'uint16', name: 'typeId', type: 'uint16' },
                    { internalType: 'uint8', name: 'position', type: 'uint8' },
                    { internalType: 'int24', name: 'line', type: 'int24' },
                  ],
                  internalType: 'struct ISportsAMMV2.CombinedPosition[][]',
                  name: 'combinedPositions',
                  type: 'tuple[][]',
                },
              ],
              internalType: 'struct ISportsAMMV2.TradeData[]',
              name: '_tradeData',
              type: 'tuple[]',
            },
            { internalType: 'uint256', name: '_buyInAmount', type: 'uint256' },
            {
              internalType: 'uint256',
              name: '_expectedQuote',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: '_additionalSlippage',
              type: 'uint256',
            },
            { internalType: 'address', name: '_referrer', type: 'address' },
            { internalType: 'address', name: '_collateral', type: 'address' },
          ],
          indexed: false,
          internalType: 'struct ISGPTradingProcessor.SGPTradeData',
          name: 'sgpTradeData',
          type: 'tuple',
        },
        {
          indexed: false,
          internalType: 'uint256',
          name: '_approvedQuote',
          type: 'uint256',
        },
        {
          indexed: false,
          internalType: 'uint256',
          name: 'timestamp',
          type: 'uint256',
        },
      ],
      name: 'SGPTradeFulfilled',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'address',
          name: 'requester',
          type: 'address',
        },
        {
          indexed: false,
          internalType: 'uint256',
          name: 'requestCounter',
          type: 'uint256',
        },
        {
          indexed: false,
          internalType: 'bytes32',
          name: 'requestId',
          type: 'bytes32',
        },
        {
          components: [
            {
              components: [
                { internalType: 'bytes32', name: 'gameId', type: 'bytes32' },
                { internalType: 'uint16', name: 'sportId', type: 'uint16' },
                { internalType: 'uint16', name: 'typeId', type: 'uint16' },
                { internalType: 'uint256', name: 'maturity', type: 'uint256' },
                { internalType: 'uint8', name: 'status', type: 'uint8' },
                { internalType: 'int24', name: 'line', type: 'int24' },
                { internalType: 'uint24', name: 'playerId', type: 'uint24' },
                { internalType: 'uint256[]', name: 'odds', type: 'uint256[]' },
                {
                  internalType: 'bytes32[]',
                  name: 'merkleProof',
                  type: 'bytes32[]',
                },
                { internalType: 'uint8', name: 'position', type: 'uint8' },
                {
                  components: [
                    { internalType: 'uint16', name: 'typeId', type: 'uint16' },
                    { internalType: 'uint8', name: 'position', type: 'uint8' },
                    { internalType: 'int24', name: 'line', type: 'int24' },
                  ],
                  internalType: 'struct ISportsAMMV2.CombinedPosition[][]',
                  name: 'combinedPositions',
                  type: 'tuple[][]',
                },
              ],
              internalType: 'struct ISportsAMMV2.TradeData[]',
              name: '_tradeData',
              type: 'tuple[]',
            },
            { internalType: 'uint256', name: '_buyInAmount', type: 'uint256' },
            {
              internalType: 'uint256',
              name: '_expectedQuote',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: '_additionalSlippage',
              type: 'uint256',
            },
            { internalType: 'address', name: '_referrer', type: 'address' },
            { internalType: 'address', name: '_collateral', type: 'address' },
          ],
          indexed: false,
          internalType: 'struct ISGPTradingProcessor.SGPTradeData',
          name: 'sgpTradeData',
          type: 'tuple',
        },
      ],
      name: 'SGPTradeRequested',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'address',
          name: '_freeBetsHolder',
          type: 'address',
        },
      ],
      name: 'SetFreeBetsHolder',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'uint256',
          name: '_maxAllowedExecutionDelay',
          type: 'uint256',
        },
      ],
      name: 'SetMaxAllowedExecutionDelay',
      type: 'event',
    },
    {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: 'address',
          name: 'account',
          type: 'address',
        },
      ],
      name: 'Unpaused',
      type: 'event',
    },
    {
      inputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      name: 'counterToRequestId',
      outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [
        { internalType: 'bytes32', name: '_requestId', type: 'bytes32' },
        { internalType: 'bool', name: '_allow', type: 'bool' },
        { internalType: 'uint256', name: '_approvedQuote', type: 'uint256' },
      ],
      name: 'fulfillSGPTrade',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [],
      name: 'jobSpecId',
      outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [],
      name: 'maxAllowedExecutionDelay',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [],
      name: 'owner',
      outputs: [{ internalType: 'address', name: '', type: 'address' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [],
      name: 'paused',
      outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [],
      name: 'paymentAmount',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [],
      name: 'renounceOwnership',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [],
      name: 'requestCounter',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      name: 'requestIdFulfilled',
      outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      name: 'requestIdToFulfillAllowed',
      outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      name: 'requestIdToRequester',
      outputs: [{ internalType: 'address', name: '', type: 'address' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      name: 'requestIdToTradeData',
      outputs: [
        { internalType: 'uint256', name: '_buyInAmount', type: 'uint256' },
        { internalType: 'uint256', name: '_expectedQuote', type: 'uint256' },
        {
          internalType: 'uint256',
          name: '_additionalSlippage',
          type: 'uint256',
        },
        { internalType: 'address', name: '_referrer', type: 'address' },
        { internalType: 'address', name: '_collateral', type: 'address' },
      ],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [
        {
          components: [
            {
              components: [
                { internalType: 'bytes32', name: 'gameId', type: 'bytes32' },
                { internalType: 'uint16', name: 'sportId', type: 'uint16' },
                { internalType: 'uint16', name: 'typeId', type: 'uint16' },
                { internalType: 'uint256', name: 'maturity', type: 'uint256' },
                { internalType: 'uint8', name: 'status', type: 'uint8' },
                { internalType: 'int24', name: 'line', type: 'int24' },
                { internalType: 'uint24', name: 'playerId', type: 'uint24' },
                { internalType: 'uint256[]', name: 'odds', type: 'uint256[]' },
                {
                  internalType: 'bytes32[]',
                  name: 'merkleProof',
                  type: 'bytes32[]',
                },
                { internalType: 'uint8', name: 'position', type: 'uint8' },
                {
                  components: [
                    { internalType: 'uint16', name: 'typeId', type: 'uint16' },
                    { internalType: 'uint8', name: 'position', type: 'uint8' },
                    { internalType: 'int24', name: 'line', type: 'int24' },
                  ],
                  internalType: 'struct ISportsAMMV2.CombinedPosition[][]',
                  name: 'combinedPositions',
                  type: 'tuple[][]',
                },
              ],
              internalType: 'struct ISportsAMMV2.TradeData[]',
              name: '_tradeData',
              type: 'tuple[]',
            },
            { internalType: 'uint256', name: '_buyInAmount', type: 'uint256' },
            {
              internalType: 'uint256',
              name: '_expectedQuote',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: '_additionalSlippage',
              type: 'uint256',
            },
            { internalType: 'address', name: '_referrer', type: 'address' },
            { internalType: 'address', name: '_collateral', type: 'address' },
          ],
          internalType: 'struct ISGPTradingProcessor.SGPTradeData',
          name: '_sgpTradeData',
          type: 'tuple',
        },
      ],
      name: 'requestSGPTrade',
      outputs: [
        { internalType: 'bytes32', name: 'requestId', type: 'bytes32' },
      ],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [
        { internalType: 'address', name: '_link', type: 'address' },
        { internalType: 'address', name: '_oracle', type: 'address' },
        { internalType: 'address', name: '_sportsAMM', type: 'address' },
        { internalType: 'bytes32', name: '_jobSpecId', type: 'bytes32' },
        { internalType: 'uint256', name: '_paymentAmount', type: 'uint256' },
      ],
      name: 'setConfiguration',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [
        { internalType: 'address', name: '_freeBetsHolder', type: 'address' },
      ],
      name: 'setFreeBetsHolder',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [
        {
          internalType: 'uint256',
          name: '_maxAllowedExecutionDelay',
          type: 'uint256',
        },
      ],
      name: 'setMaxAllowedExecutionDelay',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'bool', name: '_setPausing', type: 'bool' }],
      name: 'setPaused',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [],
      name: 'sportsAMM',
      outputs: [
        { internalType: 'contract ISportsAMMV2', name: '', type: 'address' },
      ],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
      name: 'timestampPerRequest',
      outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
      name: 'transferOwnership',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      inputs: [
        { internalType: 'address', name: 'collateral', type: 'address' },
        { internalType: 'address', name: 'recipient', type: 'address' },
      ],
      name: 'withdrawCollateral',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
  ],
}

export default sgpTradingProcessorContract
